const get = require('lodash/get');

const DEFAULT_ODIN_FLAG_KEY = 'odinActive';
const DEFAULT_ODIN_CALLBACK_PATH = '/auth/callback';
const DEFAULT_ODIN_ALLOWED_ORIGINS = [
  'http://127.0.0.1:4200',
  'http://localhost:4200',
];
const ALLOWED_ODIN_CALLBACK_PATHS = new Set([
  '/auth/callback',
  '/authoring/auth/callback',
  '/odin/auth/callback',
]);
const ALLOWED_ODIN_HANDOFF_INIT_PATHS = new Set([
  '/auth/handoff/init',
  '/odin/auth/handoff/init',
]);

const TRUE_STRING_VALUES = new Set(['1', 'true', 'yes', 'on']);

const readEnvValue = (value) => (typeof value === 'string' ? value.trim() : '');

const currentOrigin = () => {
  if (typeof window === 'undefined' || !window.location) {
    return 'http://localhost';
  }
  return window.location.origin;
};

const normalizeBooleanFlag = (value) => {
  if (typeof value === 'string') {
    return TRUE_STRING_VALUES.has(value.trim().toLowerCase());
  }
  return !!value;
};

const trimTrailingSlash = (value) => value.replace(/\/+$/, '');

const getOdinFlagKey = () => {
  return readEnvValue(process.env.VUE_APP_ODIN_USER_FLAG) || DEFAULT_ODIN_FLAG_KEY;
};

const hasOdinAccess = (user, flagKey = getOdinFlagKey()) => {
  if (!user || !flagKey) {
    return false;
  }

  return [
    get(user, [`https://vrpatients.com/${flagKey}`]),
    get(user, ['https://vrpatients.com/app_metadata', flagKey]),
    get(user, ['app_metadata', flagKey]),
  ].some(normalizeBooleanFlag);
};

const canAccessOdin = (user, flagKey = getOdinFlagKey()) => {
  if (!user) {
    return false;
  }

  return user.activeRole === 'SUPER_ADMIN' || hasOdinAccess(user, flagKey);
};

const getConfiguredOdinCallbackTarget = () => {
  const configuredCallbackTarget = readEnvValue(process.env.VUE_APP_ODIN_CALLBACK_TARGET);
  if (configuredCallbackTarget) {
    return configuredCallbackTarget;
  }

  const configuredBaseUrl = readEnvValue(process.env.VUE_APP_ODIN_BASE_URL);
  if (!configuredBaseUrl) {
    return '';
  }

  return `${trimTrailingSlash(configuredBaseUrl)}${DEFAULT_ODIN_CALLBACK_PATH}`;
};

const getConfiguredOdinOrigins = ({
  callbackTarget = getConfiguredOdinCallbackTarget(),
  allowedOrigins = readEnvValue(process.env.VUE_APP_ODIN_ALLOWED_ORIGINS),
  origin = currentOrigin(),
} = {}) => {
  const origins = new Set(DEFAULT_ODIN_ALLOWED_ORIGINS);

  if (origin) {
    origins.add(origin);
  }

  allowedOrigins
    .split(',')
    .map(candidate => candidate.trim())
    .filter(Boolean)
    .forEach(candidate => origins.add(candidate));

  if (callbackTarget) {
    try {
      origins.add(new URL(callbackTarget, origin).origin);
    } catch (error) {
      // Ignore malformed callback targets when building the trusted origin set.
    }
  }

  return origins;
};

const getOdinHandoffUrl = ({
  callbackTarget = getConfiguredOdinCallbackTarget(),
  state,
  origin = currentOrigin(),
} = {}) => {
  if (!callbackTarget) {
    return '';
  }

  const handoffUrl = new URL('/authoring/auth/odin-handoff', origin);
  handoffUrl.searchParams.set('target', callbackTarget);

  if (state) {
    handoffUrl.searchParams.set('state', state);
  }

  return `${handoffUrl.pathname}${handoffUrl.search}`;
};

const resolveTrustedOdinCallbackUrl = (
  target,
  {
    callbackTarget = getConfiguredOdinCallbackTarget(),
    allowedOrigins = readEnvValue(process.env.VUE_APP_ODIN_ALLOWED_ORIGINS),
    origin = currentOrigin(),
  } = {},
) => {
  const callbackUrl = new URL(target, origin);
  const trustedOrigins = getConfiguredOdinOrigins({ callbackTarget, allowedOrigins, origin });

  if (!trustedOrigins.has(callbackUrl.origin)) {
    throw new Error('Untrusted Odin callback origin.');
  }
  if (!ALLOWED_ODIN_CALLBACK_PATHS.has(callbackUrl.pathname)) {
    throw new Error('Untrusted Odin callback path.');
  }

  callbackUrl.hash = '';
  return callbackUrl;
};

const buildOdinCallbackUrl = (
  target,
  authResult,
  state,
  options = {},
) => {
  const callbackUrl = resolveTrustedOdinCallbackUrl(target, options);
  const hashParams = new URLSearchParams();

  if (authResult && authResult.idToken) {
    hashParams.set('id_token', authResult.idToken);
  }
  if (authResult && authResult.accessToken) {
    hashParams.set('access_token', authResult.accessToken);
  }
  if (authResult && authResult.expiresIn) {
    hashParams.set('expires_in', String(authResult.expiresIn));
  }
  if (authResult && authResult.tokenType) {
    hashParams.set('token_type', authResult.tokenType);
  }
  if (authResult && authResult.scope) {
    hashParams.set('scope', authResult.scope);
  }
  if (state) {
    hashParams.set('state', state);
  }

  callbackUrl.hash = hashParams.toString();
  return callbackUrl.toString();
};

const resolveTrustedOdinHandoffInitUrl = (
  handoff,
  {
    callbackTarget = getConfiguredOdinCallbackTarget(),
    allowedOrigins = readEnvValue(process.env.VUE_APP_ODIN_ALLOWED_ORIGINS),
    origin = currentOrigin(),
  } = {},
) => {
  const handoffUrl = new URL(handoff, origin);
  const trustedOrigins = getConfiguredOdinOrigins({ callbackTarget, allowedOrigins, origin });

  if (!trustedOrigins.has(handoffUrl.origin)) {
    throw new Error('Untrusted Odin handoff origin.');
  }
  if (!ALLOWED_ODIN_HANDOFF_INIT_PATHS.has(handoffUrl.pathname)) {
    throw new Error('Untrusted Odin handoff path.');
  }

  handoffUrl.hash = '';
  return handoffUrl;
};

const deriveOdinHandoffInitUrl = (
  target,
  explicitHandoff,
  options = {},
) => {
  if (explicitHandoff) {
    return resolveTrustedOdinHandoffInitUrl(explicitHandoff, options).toString();
  }

  const callbackUrl = resolveTrustedOdinCallbackUrl(target, options);
  let handoffPath = '';
  if (callbackUrl.pathname === '/auth/callback' || callbackUrl.pathname === '/authoring/auth/callback') {
    handoffPath = '/auth/handoff/init';
  } else if (callbackUrl.pathname === '/odin/auth/callback') {
    handoffPath = '/odin/auth/handoff/init';
  } else {
    throw new Error('Untrusted Odin callback path.');
  }

  callbackUrl.pathname = handoffPath;
  callbackUrl.search = '';
  callbackUrl.hash = '';
  return callbackUrl.toString();
};

module.exports = {
  ALLOWED_ODIN_HANDOFF_INIT_PATHS,
  ALLOWED_ODIN_CALLBACK_PATHS,
  DEFAULT_ODIN_FLAG_KEY,
  DEFAULT_ODIN_CALLBACK_PATH,
  DEFAULT_ODIN_ALLOWED_ORIGINS,
  buildOdinCallbackUrl,
  getOdinFlagKey,
  hasOdinAccess,
  canAccessOdin,
  deriveOdinHandoffInitUrl,
  getConfiguredOdinCallbackTarget,
  getConfiguredOdinOrigins,
  getOdinHandoffUrl,
  resolveTrustedOdinCallbackUrl,
  resolveTrustedOdinHandoffInitUrl,
}
